import React, {useCallback, useEffect, useState} from 'react';
import {useHistory} from 'react-router';
import {Card, Alert, Form, Button, Modal} from 'react-bootstrap';
import {useForm} from 'react-hook-form';

import PageTitle from 'components/ui/PageTitle/PageTitle';
import Loader from 'components/ui/AmnisLoader/AmnisLoader';
import {useAuth} from 'context/auth-context';
import {useGetBusinessPartnersList} from 'api/business-partner';
import {useConnectInvitedBpRequest} from 'api/contact-invite';
import {useUpdateUser} from 'api/users';
import {handleFormError} from 'helpers/handleFormError';
import {PATH} from 'constants/common';
import {
    BusinessPartnerListItem,
    IConnectInviteBusinessPartner,
} from 'types/api';
import {ConfirmationModal} from 'components/modals';
import {translate} from 'helpers/localize';
import {useResolveInviteStore} from 'components/pages/ResolveInvitePage/resolveInviteStore';
import {useGetInviteData} from 'api/register';
import {useContactsRequest} from 'api/contacts';

import styles from './ContactInvitePage.module.sass';
import SelectBusinessPartnerContact from './SelectBusinessPartnerContact';

const ContactInvitePage = () => {
    const hash = useResolveInviteStore(state => state.hash);
    const type = useResolveInviteStore(state => state.type);
    const reset = useResolveInviteStore(state => state.reset);
    const [showModal, setShowModal] = useState<boolean>(false);
    const history = useHistory();
    const {data: inviteData, isLoading: isInviteDataLoading} = useGetInviteData(
        {hash, type},
    );

    const {user} = useAuth();
    const {mutate: connectInvitedBp, isPending: isLoading} =
        useConnectInvitedBpRequest();
    const {mutate: updateUser} = useUpdateUser();
    const [notOnboarded, setNotOnboarded] = React.useState<boolean>(false);

    const {data: businessPartners, isLoading: isBpLoading} =
        useGetBusinessPartnersList({
            user,
            onlyDirectAccessWithoutOnlyCardUser: true,
        });

    const {
        register,
        control,
        watch,
        errors,
        setError,
        clearErrors,
        handleSubmit,
    } = useForm({
        shouldUnregister: false,
    });

    const {contact, selectedBp} = watch();

    const {data: businessPartnerContacts} = useContactsRequest();

    useEffect(() => {
        if (businessPartners) {
            const notOnboardedList = businessPartners.filter(bp => {
                return bp.sandbox || !bp.identified;
            });

            // logged-in user has only BPs with the flag sandbox=true and identified=false
            if (notOnboardedList.length === businessPartners.length) {
                updateUser({'@id': user['@id'], contactInviteHash: hash});
                setNotOnboarded(true);
            }
        }
        if (businessPartners && businessPartners.length === 1) {
            control.setValue('selectedBp', businessPartners[0].id);
        }
    }, [businessPartners, control, hash, updateUser, user]);

    const handleFormSubmit = (values: any) => {
        if (!hash) {
            throw new Error('Hash is missing: ' + hash);
        }
        if (!values.selectedBp) {
            setError('formError', {
                message: 'Please select a BusinessPartner first.',
            });
            return;
        }
        const request = {
            connectExistingContact: !values.contact
                ? null
                : values.contact['@id'].split('/').pop(),
            invitedBusinessPartner: `/web_api/business_partners/${values.selectedBp}`,
        };

        connectInvitedBp(
            {id: hash, data: request},
            {
                onSuccess: _ => {
                    reset();
                    history.push(PATH.DASHBOARD);
                },
                onError: error => {
                    handleFormError(error, setError);
                },
            },
        );
    };

    const handleCreateNew = () => {
        handleFormSubmit({selectedBp});
    };

    const ackInactiveInvite = () => {
        reset();
        history.push(PATH.DASHBOARD);
    };

    const handleSkip = () => {
        ackInactiveInvite();
    };

    const handleCancel = () => {
        setShowModal(false);
    };

    const renderMessage = useCallback(
        () => (
            <Alert variant="info" className="text-center">
                {inviteData?.inviterName ? (
                    <>
                        <span className="font-weight-bold">
                            {inviteData.inviterName}
                        </span>{' '}
                        from{' '}
                    </>
                ) : null}
                <span className="font-weight-bold">
                    {inviteData?.companyName}
                </span>{' '}
                invited you to connect. Complete the onboarding to pay and
                receive around the clock instantly through the free Amnis PEER
                network!
            </Alert>
        ),
        [inviteData],
    );

    const handleSelectedBpChange = () => {
        control.setValue('contact', '');
        clearErrors();
    };

    const renderForm = () => (
        <>
            {inviteData ? (
                <Alert variant="info" className="text-center">
                    {inviteData.inviterName ? (
                        <>
                            <span className="font-weight-bold">
                                <var data-var="inviterName">
                                    {inviteData.inviterName}
                                </var>
                            </span>{' '}
                            from{' '}
                        </>
                    ) : null}
                    <span className="font-weight-bold">
                        <var data-var="companyName">
                            {inviteData.companyName}
                        </var>
                    </span>{' '}
                    invited you to connect. Please accept to pay and receive
                    around the clock instantly through the free Amnis PEER
                    network!
                </Alert>
            ) : null}
            <form
                className={styles.from}
                onChange={() =>
                    (errors.formError || errors.contact) && clearErrors()
                }
            >
                {businessPartners && businessPartners.length > 1
                    ? businessPartners.map((bp: BusinessPartnerListItem) => {
                          return !bp.sandbox ? (
                              <div
                                  className="d-flex"
                                  key={bp.id}
                                  data-notranslate
                              >
                                  <Form.Check
                                      key={bp.id}
                                      id={`${bp.id}`}
                                      ref={register}
                                      label={bp.companyName}
                                      value={bp.id}
                                      name="selectedBp"
                                      className={styles.bpItemContact}
                                      onChange={handleSelectedBpChange}
                                      type="radio"
                                      disabled={
                                          !bp.identified ||
                                          (inviteData &&
                                              Number(
                                                  (
                                                      inviteData as IConnectInviteBusinessPartner
                                                  ).companyId,
                                              ) === bp.id)
                                      }
                                  />
                                  {!bp.identified ? (
                                      <span className="font-italic">
                                          {' '}
                                          - {translate('not yet identified')}
                                      </span>
                                  ) : null}
                              </div>
                          ) : null;
                      })
                    : null}
                {errors.formError && (
                    <Alert variant="danger" className={styles.alert}>
                        {errors.formError.message}
                    </Alert>
                )}
                <SelectBusinessPartnerContact
                    contacts={businessPartnerContacts || []}
                    control={control}
                    errors={errors}
                    inviter={inviteData?.companyName}
                    handleCreateNew={handleCreateNew}
                />
                <div className="d-flex justify-between">
                    <button
                        type="button"
                        className={styles.skipButton}
                        onClick={handleSkip}
                    >
                        Skip for now
                    </button>
                    <Button
                        disabled={!contact || isLoading}
                        onClick={() => setShowModal(true)}
                        className={styles.submitButton}
                    >
                        Save
                    </Button>
                </div>
            </form>
        </>
    );

    const renderContent = () => {
        if (inviteData && !inviteData.active) {
            ackInactiveInvite();
            return null;
        }
        if (notOnboarded) {
            return renderMessage();
        }
        return renderForm();
    };

    if (!businessPartners || isBpLoading || isInviteDataLoading) {
        return <Loader />;
    }

    return (
        <Card className="mt-4" body>
            <PageTitle description="You got invited to connect via PEER. Please select one of your existing contacts or create a new contact.">
                PEER-Network invitation
            </PageTitle>
            {renderContent()}
            <Modal show={showModal} onHide={handleCancel}>
                <ConfirmationModal
                    title="Confirmation"
                    description={translate(
                        `Are you sure you want to connect the invite with the company %{contactName}?`,
                        {
                            contactName: contact?.name,
                        },
                    )}
                    translateDescription={false}
                    onClose={handleCancel}
                    onSubmit={handleSubmit(handleFormSubmit)}
                />
            </Modal>
        </Card>
    );
};

export default ContactInvitePage;
